import { useUnsavedChanges } from "../../layout/AppShellContext";
import { useActiveCompanyId } from "../../api/useActiveCompanyId";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import { MobileMenu } from "../Dashboard/MobileMenu";
import { api } from "../../api/client";
import { uploadFilesToApi } from "../shared/uploadFiles";
import { FileUploadField } from "../shared/FileUploadField";
import "../shared/mobileSection.scss";

type DesignInfoResponse = {
    data?: {
        address?: Array<{ id: number; address: string }>;
        view?: string[];
        format?: string[];
        selectedCompanyId?: number | null;
    };
};

type CreateResponse = {
    data?: {
        id?: number;
    };
};

export const DesignTicketCreatePage: React.FC = () => {
    const { value: activeCompanyId } = useActiveCompanyId();
    const generation = useRef(0);
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [companies, setCompanies] = useState<Array<{ id: number; address: string }>>([]);
    const [views, setViews] = useState<string[]>([]);
    const [formats, setFormats] = useState<string[]>([]);
    const [companyId, setCompanyId] = useState("");
    const [taskType, setTaskType] = useState("");
    const [layoutRequired, setLayoutRequired] = useState("Новый макет");
    const [layoutFormat, setLayoutFormat] = useState("");
    const [visualizationRequired, setVisualizationRequired] = useState("Не нужна");
    const [vkLink, setVkLink] = useState("");
    const [comment, setComment] = useState("");
    const [visualizationFiles, setVisualizationFiles] = useState<File[]>([]);
    const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
    const [selectionBaseline, setSelectionBaseline] = useState("");

    useUnsavedChanges(!loading && !success && (saving || JSON.stringify([companyId, taskType, layoutRequired, layoutFormat, visualizationRequired]) !== selectionBaseline || !!vkLink.trim() || !!comment.trim() || visualizationFiles.length > 0 || additionalFiles.length > 0), "Заказ дизайна");
    useEffect(() => {
        let alive = true; const counter = generation; ++counter.current;
        setError(null); setSuccess(null); setVkLink(""); setComment(""); setVisualizationFiles([]); setAdditionalFiles([]);
        setLoading(true); setSaving(false); setLayoutRequired("Новый макет"); setVisualizationRequired("Не нужна");
        api.get<DesignInfoResponse>("/api/design/gateway/tasks/info")
            .then((response) => {
                if (!alive) {
                    return;
                }

                const nextCompanies = response?.data?.address ?? [];
                const nextViews = response?.data?.view ?? [];
                const nextFormats = response?.data?.format ?? [];
                const defaultCompanyId = response?.data?.selectedCompanyId;

                setCompanies(nextCompanies.map((item) => ({ id: item.id, address: item.address })));
                setViews(nextViews);
                setFormats(nextFormats);
                setCompanyId(defaultCompanyId ? String(defaultCompanyId) : (nextCompanies[0]?.id ? String(nextCompanies[0].id) : ""));
                setSelectionBaseline(JSON.stringify([defaultCompanyId ? String(defaultCompanyId) : String(nextCompanies[0]?.id || ""), nextViews[0] ?? "", "Новый макет", nextFormats[0] ?? "", "Не нужна"]));
                setTaskType(nextViews[0] ?? "");
                setLayoutFormat(nextFormats[0] ?? "");
            })
            .catch((requestError: any) => {
                if (alive) {
                    setError(requestError?.message ?? "Не удалось загрузить форму");
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });

        return () => {
            alive = false;
            ++counter.current;
        };
    }, [activeCompanyId]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (saving || success) return;
        const epoch = generation.current; const selectedCompany = activeCompanyId;
        if (!companyId || !taskType || !layoutRequired || !layoutFormat) {
            setError("Заполните точку, тип задачи и формат");
            return;
        }

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const uploadedVisualizationFiles = visualizationFiles.length > 0
                ? await uploadFilesToApi(visualizationFiles)
                : [];
            const uploadedAdditionalFiles = additionalFiles.length > 0
                ? await uploadFilesToApi(additionalFiles)
                : [];

            if (epoch !== generation.current) return;
            const response = await api.post<CreateResponse>("/api/design/gateway/tasks", {
                companyId: Number(companyId),
                taskType,
                layoutRequired,
                layoutFormat,
                visualizationRequired,
                vkLink: vkLink.trim(),
                comment: comment.trim(),
                visualizationFiles: uploadedVisualizationFiles,
                additionalFiles: uploadedAdditionalFiles,
            }, { headers: { "X-Company-Id": selectedCompany || companyId } });

            if (epoch !== generation.current) return;
            setSuccess("Задача создана");
            const nextId = response?.data?.id;
            window.setTimeout(() => {
                if (epoch !== generation.current) return;
                navigate(nextId ? `/order-layout/ticket/${nextId}` : "/order-layout");
            }, 300);
        } catch (submitError: any) {
            if (epoch !== generation.current) return;
            setError(submitError?.message ?? "Не удалось создать задачу");
        } finally {
            if (epoch === generation.current) setSaving(false);
        }
    };

    return (
        <div className="section-page">
            <MenuHeader onMenuClick={() => setMenuOpen(true)} />
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

            <h1 className="section-title">Новый заказ дизайна</h1>

            {loading ? <div className="section-empty">Загрузка формы...</div> : null}
            {error ? <div className="section-error">{error}</div> : null}
            {success ? <div className="section-success">{success}</div> : null}

            {!loading ? (
                <div className="section-card">
                    <form className="section-form" onSubmit={handleSubmit}>
                        <div className="section-field">
                            <label className="section-label" htmlFor="design-field-1">Точка</label>
                            <select id="design-field-1"
                                className="section-select"
                                value={companyId}
                                onChange={(event) => setCompanyId(event.target.value)}
                            >
                                {companies.map((company) => (
                                    <option key={company.id} value={company.id}>
                                        {company.address}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="section-field">
                            <label className="section-label" htmlFor="design-field-2">Тип рекламного оформления</label>
                            <select id="design-field-2"
                                className="section-select"
                                value={taskType}
                                onChange={(event) => setTaskType(event.target.value)}
                            >
                                {views.map((item) => (
                                    <option key={item} value={item}>
                                        {item}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="section-inline">
                            <div className="section-field">
                                <label className="section-label" htmlFor="design-field-3">Какой нужен макет</label>
                                <select id="design-field-3"
                                    className="section-select"
                                    value={layoutRequired}
                                    onChange={(event) => setLayoutRequired(event.target.value)}
                                >
                                    <option value="Новый макет">Новый макет</option>
                                    <option value="Адаптация существующего">Адаптация существующего</option>
                                    <option value="Корректировка">Корректировка</option>
                                </select>
                            </div>

                            <div className="section-field">
                                <label className="section-label" htmlFor="design-field-4">Формат</label>
                                <select id="design-field-4"
                                    className="section-select"
                                    value={layoutFormat}
                                    onChange={(event) => setLayoutFormat(event.target.value)}
                                >
                                    {formats.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="section-field">
                            <label className="section-label" htmlFor="design-field-5">Нужна ли визуализация</label>
                            <select id="design-field-5"
                                className="section-select"
                                value={visualizationRequired}
                                onChange={(event) => setVisualizationRequired(event.target.value)}
                            >
                                <option value="Не нужна">Не нужна</option>
                                <option value="Нужна">Нужна</option>
                            </select>
                        </div>

                        <div className="section-field">
                            <label className="section-label" htmlFor="design-field-6">Ссылка на VK / соцсети</label>
                            <input id="design-field-6"
                                className="section-input"
                                value={vkLink}
                                onChange={(event) => setVkLink(event.target.value)}
                                placeholder="https://vk.com/..."
                            />
                        </div>

                        <div className="section-field">
                            <label className="section-label" htmlFor="design-field-7">Комментарий</label>
                            <textarea id="design-field-7"
                                className="section-textarea"
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                placeholder="Что нужно учесть дизайнеру"
                            />
                        </div>

                        <FileUploadField
                            label="Файлы для визуализации"
                            files={visualizationFiles}
                            disabled={saving}
                            onChange={setVisualizationFiles}
                            hint="Референсы, фото точки, примеры размещения"
                        />

                        <FileUploadField
                            label="Дополнительные файлы"
                            files={additionalFiles}
                            disabled={saving}
                            onChange={setAdditionalFiles}
                            hint="Логотипы, исходники, бренд-материалы"
                        />

                        <div className="section-actions">
                            <button className="section-btn" type="submit" disabled={saving}>
                                {saving ? "Создаем..." : "Создать задачу"}
                            </button>
                            <button
                                className="section-btn-secondary"
                                type="button"
                                disabled={saving}
                                onClick={() => navigate("/order-layout")}
                            >
                                Отмена
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
};
